import React, { useState, useEffect, useCallback, useRef } from "react";
import { useNavigate, useParams } from "react-router-dom";
import useMediaQuery from "@mui/material/useMediaQuery";

import { OverviewTable } from "./overview/overview-table";
import OperationalDashboard from "../../../service/operationalDashboard";
import { navDatasets, navEntities } from "../../kmk/AppData";
import Tabs from "@mui/material/Tabs";
import Tab from "@mui/material/Tab";
import Box from "@mui/material/Box";
import Tooltip from "@mui/material/Tooltip";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import IconButton from "@mui/material/IconButton";

const tableHeader = [
  {
    key: "Name",
    value: "DisplayName",
    sortable: true,
  },
  {
    key: "Current Records",
    value: "Current",
    sortable: true,
  },
  {
    key: "New Records",
    value: "New",
    sortable: true,
  },
  {
    key: "Updated Records",
    value: "Update",
    sortable: true,
  },
  {
    key: "Deleted Records",
    value: "Delete",
    sortable: true,
  },
  {
    key: "Last Data Load",
    value: "RecDate",
    sortable: true,
    showTimezone: true,
  },
  {
    key: "Data Health",
    value: "DQM_DataHealth",
    progressbar: true,
    format: "=value=%",
    link: true,
    linkTo: "./dqm",
    linkDataKey: "Name",
  },
];

const tableEntityHeader = [
  {
    key: "Name",
    value: "DisplayName",
    sortable: true,
  },
  {
    key: "Current Records",
    value: "Current",
    sortable: true,
  },
  {
    key: "New Records",
    value: "New",
    sortable: true,
  },
  {
    key: "Updated Records",
    value: "Update",
    sortable: true,
  },
  {
    key: "Pending Cases",
    value: "Pending",
    sortable: true,
  },
  {
    key: "Last MDM Run",
    value: "RecDate",
    sortable: true,
    showTimezone: true,
  },
];

const opdForDataset = new OperationalDashboard();
const opdForEntity = new OperationalDashboard();

const TopDataSets = () => {
  const { Workspace_id } = useParams();
  const [dscount, setDSCount] = useState(0);
  const [encount, setENCount] = useState(0);
  const [topDataSources, setTopDataSources] = useState([]);
  const [topEntities, setTopEntities] = useState([]);
  const [loading, setLoading] = useState(false);
  const [activeTab, setActiveTab] = useState(() => {
    return localStorage.getItem(`activeTab_${Workspace_id}`) || "dataSource";
  });

  const isSmallScreen = useMediaQuery((theme) => theme.breakpoints.down("sm"));

  const optionsRef = useRef({ orderBy: "DisplayName", order: "asc", page: 0, limit: 5 });
  const changedRef = useRef({ orderBy: "DisplayName", order: "asc", page: 0, limit: 5 });

  const navigate = useNavigate();

  const handleTabChange = (event, newValue) => {
    setActiveTab(newValue);
    optionsRef.current.page = 0;
    [optionsRef.current.orderBy, changedRef.current.orderBy] = [changedRef.current.orderBy, optionsRef.current.orderBy];
    [optionsRef.current.order, changedRef.current.order] = [changedRef.current.order, optionsRef.current.order];
    [optionsRef.current.page, changedRef.current.page] = [changedRef.current.page, optionsRef.current.page];
    [optionsRef.current.limit, changedRef.current.limit] = [changedRef.current.limit, optionsRef.current.limit];

    localStorage.setItem(`activeTab_${Workspace_id}`, newValue);
  };

  const getTopDataSourceList = useCallback(
    async (loadFlag = false) => {
      if (!loadFlag) setLoading(true);

      setTopDataSources([]);
      setDSCount(0);

      const result = await opdForDataset.getTopDataSourceList(Workspace_id, {
        ...optionsRef.current,
      });

      if (result?.success) {
        setTopDataSources(result.data.dataSourceList);
        setDSCount(result.data.count);
      }

      if (!loadFlag) setLoading(false);
    },
    [Workspace_id]
  );

  const getTopEntityList = useCallback(
    async (loadFlag = false) => {
      if (!loadFlag) setLoading(true);

      setTopEntities([]);
      setENCount(0);

      const entities_result = await opdForEntity.getTopEntityList(Workspace_id, {
        ...optionsRef.current,
      });

      if (entities_result?.success) {
        setTopEntities(entities_result.data.entitySourceList);
        setENCount(entities_result.data.count);
      }

      if (!loadFlag) setLoading(false);
    },
    [Workspace_id]
  );

  const handleSorting = useCallback(
    (property) => {
      const isAsc = optionsRef.current?.orderBy === property && optionsRef.current?.order === "asc";

      optionsRef.current.orderBy = property;
      optionsRef.current.order = isAsc ? "desc" : "asc";

      if (activeTab === "dataSource") getTopDataSourceList();
      else getTopEntityList();
    },
    [activeTab, getTopDataSourceList, getTopEntityList]
  );

  const navigateToDashboard = useCallback(
    (data) => {
      navigate(
        `/my-workspaces/${Workspace_id}/${activeTab === "dataSource" ? navDatasets.link : navEntities.link}/statistics`,
        { state: { Object_id: data.Object_id, fromDashboard: true } }
      );
    },
    [Workspace_id, activeTab, navigate]
  );

  useEffect(() => {
    (async () => {
      setLoading(true);
      await Promise.all([getTopDataSourceList(true), getTopEntityList(true)]);
      setLoading(false);
    })();

    return () => {
      opdForDataset.cancelRequest();
      opdForEntity.cancelRequest();
    };
  }, [getTopDataSourceList, getTopEntityList]);

  const handleChangePage = (_event, newPage) => {
    optionsRef.current.page = newPage;

    if (activeTab === "dataSource") getTopDataSourceList();
    else getTopEntityList();
  };

  const handleChangeRowsPerPage = (event) => {
    const newLimit = parseInt(event.target.value, 10);

    optionsRef.current.page = 0;
    optionsRef.current.limit = newLimit;

    if (activeTab === "dataSource") getTopDataSourceList();
    else getTopEntityList();
  };

  return (
    <div>
      {/* Tab navigation */}
      <Tabs
        value={activeTab}
        onChange={handleTabChange}
        style={{
          boxShadow: "rgba(0, 0, 0, 0.04) 0px 5px 22px, rgba(0, 0, 0, 0.03) 0px 0px 0px 0.5px",
          backgroundColor: "rgb(255, 255, 255)",
          color: "rgb(17, 25, 39)",
          transition: "box-shadow 300ms cubic-bezier(0.4, 0, 0.2, 1) 0ms",
          overflow: "hidden",
          borderRadius: "20px 20px 0 0",
          height: "100%",
          width: "100%",
          padding: "15px", // Adding padding
        }}
      >
        <Tab
          value="dataSource"
          label="Top Datasets"
          style={{ fontSize: "1.2rem", fontWeight: "bold", color: "black" }}
        />
        <Tab value="entities" label="Top Entities" style={{ fontSize: "1.2rem", fontWeight: "bold", color: "black" }} />
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "flex-end",
            color: "grey",
            width: "100%",
            whiteSpace: "break-spaces",
            wordBreak: "break-word",
            marginX: 2,
          }}
          fontSize={"0.875rem"}
        >
          {isSmallScreen ? (
            <Tooltip
              title="All records are updated as of the last Quick Import, Data Load, DQM, and MDM job runs."
              arrow
            >
              <IconButton size="small">
                <InfoOutlinedIcon fontSize="inherit" />
              </IconButton>
            </Tooltip>
          ) : (
            <span>All records are updated as of the last Quick Import, Data Load, DQM, and MDM job runs.</span>
          )}
        </Box>
      </Tabs>
      {/* Table component based on active tab */}
      {activeTab === "dataSource" && (
        <OverviewTable
          header={tableHeader}
          data={topDataSources}
          sx={{ height: "100%" }}
          count={dscount}
          limit={optionsRef.current?.limit || 5}
          handleChangePage={handleChangePage}
          handleChangeRowsPerPage={handleChangeRowsPerPage}
          page={optionsRef.current?.page || 0}
          loading={loading}
          tableHeight={420}
          handleRowClick={navigateToDashboard}
          sortingProperties={{
            orderBy: optionsRef.current?.orderBy || "",
            order: optionsRef.current?.order || "",
            handleSorting,
          }}
        />
      )}
      {activeTab === "entities" && (
        <OverviewTable
          header={tableEntityHeader}
          data={topEntities}
          sx={{ height: "100%" }}
          count={encount}
          limit={optionsRef.current?.limit || 5}
          handleChangePage={handleChangePage}
          handleChangeRowsPerPage={handleChangeRowsPerPage}
          page={optionsRef.current?.page || 0}
          loading={loading}
          tableHeight={420}
          handleRowClick={navigateToDashboard}
          sortingProperties={{
            orderBy: optionsRef.current?.orderBy || "",
            order: optionsRef.current?.order || "",
            handleSorting,
          }}
        />
      )}
    </div>
  );
};

export default TopDataSets;
